/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {startTransition, useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {useHistory} from 'react-router-dom'
// Project Components
import NestedAccordion from '../nested-accordion'
import WishlistCount from './partials/wishlist-count'

// Components
import {
    Box,
    Drawer,
    DrawerBody,
    DrawerOverlay,
    DrawerContent,
    IconButton,
    useMultiStyleConfig,
    Skeleton,
    Image,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'
// Icons

import {scheduleAfterPaint} from '../../utils/common-utils'

// Others
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

// CUSTOMIZE YOUR NAVIGATION BY ALTERING THESE VALUES
import {
    YR_HOME_HREF,
    KS_BRAND_ID,
    KS_MY_ACCOUNT_URL,
    YR_BRAND_ID,
    KS_HOME_HREF,
    BASKET_URL
} from '../../constants'
import {SearchBar} from '../header'
import ContentAsset from '../content-asset'
import {getUrl} from '../../utils/cloudinary-utils'
import LoyaltyCallouts from '../header/loyalty-callouts'
import StickyBackSection from './sticky-back-section'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useGlobaleContext} from '../../contexts/globale'
import FindStore from './find-store'
import {useSiteContext} from '../../contexts/site'
import UtilityBanner from '../utility-banner'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'

/**
 * This is the navigation component used for mobile devices (phone and tablet). It's
 * main usage is to navigate from one category to the next, but also homes links to
 * support, log in and out actions, as support links.
 */
const DrawerMenu = ({
    root,
    itemsKey,
    itemsCountKey,
    isOpen,
    setShowSpinner,
    onClose = noop,
    itemComponent
}) => {
    const {isYR} = useSiteContext()
    const {countryISO} = useGlobaleContext()
    const styles = useMultiStyleConfig('DrawerMenu')
    const [ariaBusy, setAriaBusy] = useState('true')
    const {buildUrl} = useMultiSite()
    const {data: basket} = useCurrentBasket
    const history = useHistory()
    const {
        app: {commerceAPI}
    } = getConfig()
    const {hostUrl} = commerceAPI.storefront

    useEffect(() => {
        setAriaBusy('false')
    }, [])

    const onLogoClick = () => {
        closeDrawer()
        resetDrawerState()

        // Deferred: Route navigation to next paint (lower priority)
        scheduleAfterPaint(() => {
            const path = buildUrl(isYR ? YR_HOME_HREF : KS_HOME_HREF)
            history.push(path)
        })
    }

    const onCartClick = () => {
        location.href =
            hostUrl + BASKET_URL + (basket?.basketId ? `?cartid=${basket?.basketId}` : '')
    }

    const onAccountClick = () => {
        closeDrawer()
        resetDrawerState()

        // Deferred: Navigate to account page (lower priority)
        scheduleAfterPaint(() => {
            location.href = hostUrl + KS_MY_ACCOUNT_URL
        })
    }

    const [selectedBrand, setSelectedBrand] = useState(YR_BRAND_ID)
    const [showCloseDrawer, setShowCloseDrawer] = useState(true)
    const [backSection, setShowBackSection] = useState(false)
    const [isBackSection, setBackSelection] = useState(false)
    const [isDrawerContentReady, setIsDrawerContentReady] = useState(false)
    const initialFocusRef = useRef(null)
    const closeSearchRef = useRef(null)
    const wasOpenRef = useRef(isOpen)

    const resetDrawerState = () => {
        startTransition(() => {
            setSelectedBrand(YR_BRAND_ID)
            setShowBackSection(false)
            setBackSelection(false)
            setShowCloseDrawer(true)
        })
    }

    const closeDrawer = () => startTransition(() => onClose())

    useEffect(() => {
        let isCancelled = false
        let deferredReadyId

        if (!root || isDrawerContentReady) {
            return undefined
        }

        deferredReadyId = scheduleAfterPaint(() => {
            if (!isCancelled) {
                startTransition(() => {
                    setIsDrawerContentReady(true)
                })
            }
        })

        return () => {
            isCancelled = true

            if (typeof deferredReadyId !== 'number' || typeof window === 'undefined') {
                return
            }

            if (typeof window.cancelAnimationFrame === 'function') {
                window.cancelAnimationFrame(deferredReadyId)
            } else {
                window.clearTimeout(deferredReadyId)
            }
        }
    }, [isDrawerContentReady, root])

    useEffect(() => {
        const html = document.documentElement

        if (!isOpen) {
            html.style.overflow = ''

            if (wasOpenRef.current) {
                resetDrawerState()
            }
        } else {
            html.style.overflow = 'hidden'
            // Each open starts with the expanded search row and the close button.
            resetDrawerState()
        }

        wasOpenRef.current = isOpen
    }, [isOpen])

    return (
        <Drawer
            isOpen={isOpen}
            onClose={closeDrawer}
            initialFocusRef={initialFocusRef}
            blockScrollOnMount={false}
            {...styles.drawer}
        >
            <DrawerOverlay>
                <DrawerContent {...styles.content} bg={!showCloseDrawer ? 'white' : 'gray.50'}>
                    <DrawerBody
                        {...styles.body}
                        overflowY={isOpen && isBackSection ? 'hidden' : 'auto'}
                    >
                        <Box>
                            <UtilityBanner />
                        </Box>
                        <Box
                            padding="0 12px"
                            background={isYR ? 'black.300' : 'white'}
                            height="60px"
                            alignContent="center"
                            position="sticky"
                            top="0"
                            sx={{'z-index': 1}}
                        >
                            <div ref={initialFocusRef} display="none" tabIndex="-1" />{' '}
                            {/* Dummy focus element */}
                            {!backSection && (
                                <SearchBar
                                    isMenu={true}
                                    closeSearchRef={closeSearchRef}
                                    setShowCloseDrawer={(bool) => {
                                        setShowCloseDrawer(bool)
                                    }}
                                    id="drawer"
                                    setShowSpinner={setShowSpinner}
                                    showCloseDrawer={showCloseDrawer}
                                />
                            )}
                            {backSection && (
                                <StickyBackSection
                                    onLogoClick={onLogoClick}
                                    setShowSpinner={setShowSpinner}
                                    onClick={() => {
                                        startTransition(() => setShowBackSection(false))
                                    }}
                                    onMyAccountClick={onAccountClick}
                                    onMyCartClick={onCartClick}
                                />
                            )}
                            <IconButton
                                aria-label={showCloseDrawer ? 'Close menu' : 'Close search'}
                                icon={
                                    <Image
                                        src={getUrl('UIImages/images/black-cross-icon.svg')}
                                        filter={isYR ? 'invert(1)' : undefined}
                                        width="16px"
                                        height="16px"
                                        alt=""
                                    />
                                }
                                className="close-drawer"
                                variant="unstyled"
                                position="absolute"
                                top="8px"
                                left={backSection ? '12px' : 'auto'}
                                right={backSection ? 'auto' : '12px'}
                                width="40px"
                                height="44px"
                                zIndex="2"
                                display="flex"
                                onClick={() => {
                                    if (showCloseDrawer) {
                                        closeDrawer()
                                    } else {
                                        closeSearchRef.current?.()
                                    }
                                }}
                            />
                        </Box>
                        {showCloseDrawer && (
                            <div
                                id="category-nav"
                                aria-live="polite"
                                aria-busy={ariaBusy}
                                aria-atomic="true"
                            >
                                {selectedBrand === KS_BRAND_ID && (
                                    <Box className="brand-categories" {...styles.categoriesWrapper}>
                                        {/* We don't have to display the KS category structure */}
                                    </Box>
                                )}
                                {selectedBrand === YR_BRAND_ID && (
                                    <Box
                                        className="brand-categories"
                                        {...styles.categoriesWrapper}
                                        display={selectedBrand === YR_BRAND_ID ? 'block' : 'none'}
                                    >
                                        {/* Category Navigation */}
                                        {root && isDrawerContentReady ? (
                                            <NestedAccordion
                                                allowMultiple={true}
                                                item={root}
                                                itemsCountKey={itemsCountKey}
                                                itemsKey={itemsKey}
                                                selectedBrand={selectedBrand}
                                                onClose={closeDrawer}
                                                setShowSpinner={setShowSpinner}
                                                onClick={setShowBackSection}
                                                setBackSelection={setBackSelection}
                                                itemsFilter="c_showInMenu"
                                                itemComponent={itemComponent}
                                            />
                                        ) : (
                                            <Skeleton {...styles.skeleton} />
                                        )}
                                        <Box className="custom-border-bottom" margin="0 1rem" />
                                        {countryISO === 'US' && <LoyaltyCallouts />}
                                        <Box
                                            className="ks-icons-wrapper"
                                            margin="30px 1rem 0 1rem !important"
                                        >
                                            <Link
                                                href={hostUrl + KS_MY_ACCOUNT_URL}
                                                className="user-icon"
                                                display="flex"
                                                onClick={() => {}}
                                                _hover={{textDecoration: 'none'}}
                                                marginBottom="1rem"
                                                width="fit-content"
                                                fontWeight="300"
                                                fontSize="14px"
                                                color="black.100"
                                                gap="0.8rem"
                                            >
                                                <Image
                                                    alt="User Icon"
                                                    width="16px"
                                                    height="16px"
                                                    src={getUrl(
                                                        'UIImages/images/user-circle-regular.svg'
                                                    )}
                                                />
                                                <FormattedMessage
                                                    id="account.heading.my_account"
                                                    defaultMessage="My Account"
                                                />
                                            </Link>{' '}
                                            <WishlistCount />
                                            <FindStore />
                                        </Box>
                                        <Box
                                            className="custom-border-bottom"
                                            padding="0 0 30px 0 !important"
                                            margin="0 1rem"
                                        />
                                        <Box marginBottom="2rem">
                                            <ContentAsset
                                                contentAssetId={
                                                    isYR
                                                        ? 'yr-philanthropy-shine-bright-do-good'
                                                        : 'philanthropy-shine-bright-do-good'
                                                }
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </div>
                        )}
                    </DrawerBody>
                </DrawerContent>
            </DrawerOverlay>
        </Drawer>
    )
}

DrawerMenu.displayName = 'DrawerMenu'

DrawerMenu.propTypes = {
    root: PropTypes.object,
    /**
     * The opened state of the drawer.
     */
    isOpen: PropTypes.bool,
    /**
     * Function called when the drawer is dismissed.
     */
    onClose: PropTypes.func,
    /**
     * Customize the property representing the items.
     */
    itemsKey: PropTypes.string,
    /**
     * Cusomtize the property representing the items count.
     */
    itemsCountKey: PropTypes.string,
    /**
     * Component to be rendered for each individual menu item.
     */
    itemComponent: PropTypes.elementType,
    setShowSpinner: PropTypes.func
}

export {DrawerMenu}
